import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

const userPool = new CognitoUserPool({
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
});

export type SignInResult =
  | { kind: "ok"; session: CognitoUserSession }
  | { kind: "newPasswordRequired"; user: CognitoUser; userAttributes: Record<string, string> }
  | { kind: "mfaRequired"; user: CognitoUser; challengeName: string }
  | { kind: "error"; message: string };

export async function signIn(username: string, password: string): Promise<SignInResult> {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  const auth = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise<SignInResult>((resolve) => {
    user.authenticateUser(auth, {
      onSuccess: (session) => resolve({ kind: "ok", session }),
      onFailure: (err) => resolve({ kind: "error", message: err.message ?? String(err) }),
      newPasswordRequired: (userAttributes) => {
        // Cognito 가 비교 시 사용하는 immutable 필드 제거
        delete (userAttributes as Record<string, unknown>).email_verified;
        delete (userAttributes as Record<string, unknown>).phone_number_verified;
        resolve({ kind: "newPasswordRequired", user, userAttributes });
      },
      mfaRequired: (challengeName) => resolve({ kind: "mfaRequired", user, challengeName }),
      totpRequired: (challengeName) => resolve({ kind: "mfaRequired", user, challengeName }),
    });
  });
}

export async function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string>
): Promise<SignInResult> {
  return new Promise<SignInResult>((resolve) => {
    user.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => resolve({ kind: "ok", session }),
      onFailure: (err) => resolve({ kind: "error", message: err.message ?? String(err) }),
    });
  });
}

export async function submitMfa(user: CognitoUser, code: string): Promise<SignInResult> {
  return new Promise<SignInResult>((resolve) => {
    user.sendMFACode(
      code,
      {
        onSuccess: (session) => resolve({ kind: "ok", session }),
        onFailure: (err) => resolve({ kind: "error", message: err.message ?? String(err) }),
      },
      "SOFTWARE_TOKEN_MFA"
    );
  });
}

export function getCurrentUser(): CognitoUser | null {
  return userPool.getCurrentUser();
}

export async function getSession(): Promise<CognitoUserSession | null> {
  const user = userPool.getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        resolve(null);
        return;
      }
      resolve(session);
    });
  });
}

export async function getIdToken(): Promise<string | null> {
  const session = await getSession();
  return session ? session.getIdToken().getJwtToken() : null;
}

export function signOut(): void {
  userPool.getCurrentUser()?.signOut();
}
